/** @jsxImportSource @emotion/react */
import React, { useEffect } from "react";
import { FilterDropdownButton } from "./FilterDropdownButton";
import { Filter, FiltersInterface, NETWORK_NAME_TO_DISPLAY } from "../../config/consts";
import { useTradeHistoryFilterToggle } from "../../hooks/useTradeHistoryFilterToggle";
import { FilterOptionRow } from "./elements/FilterOptionRow";
import styled from "@emotion/styled";
import { Spacer } from "components/general/Spacer/Spacer";
import { useIsMobile } from "hooks/useDeviceType";
import { Table } from "@tanstack/react-table";
import { TransactionDetail } from "types/history";
import { Chain } from "wagmi";
import { useNetworkButtonConfig } from "hooks/useNetworkButtonConfig";

export interface NetworkFilterProps {
  table: Table<TransactionDetail>;
  confirmedOptions: FiltersInterface[Filter.Network];
  setConfirmedOptions: (updatedOptions: FiltersInterface[Filter.Network]) => void;
}
export const NetworkFilter: React.FC<NetworkFilterProps> = ({
  confirmedOptions,
  setConfirmedOptions,
  table,
}) => {
  const { localOptions, setLocalOptions, toggleLocalOptions } =
    useTradeHistoryFilterToggle<Chain["name"]>();
  const isMobile = useIsMobile();
  const networkButtonConfig = useNetworkButtonConfig();
  const iconSize = isMobile ? 24 : 22;

  useEffect(() => {
    setLocalOptions(confirmedOptions);
  }, [confirmedOptions, setLocalOptions]);

  return (
    <FilterDropdownButton
      filterType={Filter.Network}
      localOptions={localOptions}
      setLocalOptions={setLocalOptions}
      confirmedOptions={confirmedOptions}
      setConfirmedOptions={setConfirmedOptions}
      table={table}
    >
      <DropdownContainer>
        {Object.entries(networkButtonConfig)
          .sort(([aId, networkA], [bId, networkB]) => {
            if (networkA == null || networkB == null) {
              return aId.localeCompare(bId);
            }
            return networkA.name.localeCompare(networkB.name);
          })
          .map(([network, props]) => {
            const Icon = props.icon;
            return (
              <FilterOptionRow
                key={network}
                onClick={() => toggleLocalOptions(NETWORK_NAME_TO_DISPLAY[props.id])}
                checked={localOptions?.includes(NETWORK_NAME_TO_DISPLAY[props.id]) ?? false}
              >
                <Content>
                  <Icon width={iconSize} height={iconSize} color={props.backgroundColor} />
                  <Spacer width="8px" />
                  {NETWORK_NAME_TO_DISPLAY[props.id]}
                </Content>
              </FilterOptionRow>
            );
          })}
      </DropdownContainer>
    </FilterDropdownButton>
  );
};

// Sub-components

const DropdownContainer = styled.div`
  min-width: 234px;
`;

const Content = styled.div`
  background: transparent;
  border: 0;
  display: flex;
  align-items: center;
`;
